import * as Sentry from '@sentry/browser';
import axios from 'axios';
import get from 'lodash/get';
import isString from 'lodash/isString';
import parse from 'html-react-parser';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import appSettings from 'src/app-settings.json';
import { ComplaintsWrapper } from 'src/app-styles';
import { ComplaintFormProvider, useComplaintForm } from 'src/app/context';
import { history } from 'src/app/history';
import { TStats } from 'src/types/types';
import { ComplaintDescription } from './complaint-description';
import { ComplaintType } from './complaint-type';
import { HeaderNote } from './complaint-form-styles';
import { FormInputTypes, IFormInputs } from './complaint-types';
import { TermsAndConditions } from './components/TermsAndConditions';
import { Disclaimer } from './disclaimer';
import { ComplainantInfo } from './modules/complainant-info/complainant-info';
import { Uploads } from './modules/file-uploads/uploads';
import { PriorComplaint } from './modules/prior-complaints/prior-complaint';
import { OffendingProperty } from './offending-property';
import { OptionalInfo } from './optional-info';
import { SubmissionModule } from './submission-module';
import { convertYesNoToBoolean } from './utils';

interface IProps {
  stats: TStats;
}

function ComplaintsFormComponent(props: IProps): JSX.Element {
  const { stats } = props;

  const { control, errors, watch, register, unregister, handleSubmit, setValue } = useForm<IFormInputs>({
    criteriaMode: 'all',
    shouldFocusError: true,
    defaultValues: {
      [FormInputTypes.tosVersionAccepted]: null,
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [typesSelected, setTypesSelected] = useState<boolean>(false);
  const [showTypeError, setShowTypeError] = useState<boolean>(false);
  const complaintTypeRef = useRef<HTMLDivElement>(null);

  const {
    complaintTypes: selectedComplaintTypeData,
    selectedComplaintTypes,
    address,
    apn,
    selectedFiles,
  } = useComplaintForm();

  const scrollToTypes = useCallback(() => complaintTypeRef?.current?.scrollIntoView({}), []);

  useEffect(() => {
    const anyTypeSelected = Object.values(selectedComplaintTypeData).some((type) => type);
    setTypesSelected(anyTypeSelected);
  }, [selectedComplaintTypeData]);

  const errorPath = '/error';
  const successPath = '/success';

  function postS3Upload(formdata: FormData) {
    return axios({
      method: 'post',
      url: `${appSettings.s3Domain}`,
      data: formdata,
    });
  }

  const memoizedOnSubmit = useCallback(
    (formData: IFormInputs): void => {
      async function axiosPostS3Token(
        complaint_types: string[],
        files: { name: string; mimetype: string }[]
      ) {
        try {
          const dataToSubmit = {
            ...convertYesNoToBoolean(formData),
            complaint_types,
            files,
            address,
            apn,
          };

          // Step 1 - POST Request to Retrieve Token
          // If the portal URL contains ?source=<value>, forward it to the API to set complaint source.
          const sourceParam = new URLSearchParams(window.location.search).get('source');
          const lodgeUrlBase = `${appSettings.strComplaintsApiDomain}/${stats?.cityInfo?.place}/lodgeComplaint`;
          const lodgeUrl =
            sourceParam != null && sourceParam !== ''
              ? `${lodgeUrlBase}?source=${encodeURIComponent(sourceParam)}`
              : lodgeUrlBase;
          const response = await axios({
            method: 'post',
            url: lodgeUrl,
            data: dataToSubmit,
          });

          const complaintId = response.data.compliant_id;
          const tokenResponse = response.data.upload_tokens;
          const tokenResponseArray = Object.entries(tokenResponse);

          // Upload all files first
          if (tokenResponseArray.length > 0) {
            try {
              await Promise.all(
                tokenResponseArray.map((e, index) => {
                  const formFields = tokenResponse[Object.keys(tokenResponse)[index]].fields;
                  const formdata = new FormData();

                  Object.keys(formFields).forEach((i) => {
                    formdata.append(i, formFields[i]);
                  });

                  formdata.append('file', selectedFiles[index], selectedFiles[index].name);
                  return postS3Upload(formdata);
                })
              );
            } catch (error) {
              Sentry.captureException(error);
              Sentry.captureMessage('Error: Error uploading attachment');
              history.push({ pathname: errorPath });
            }
          }

          // If we reach here, all uploads were successful
          history.push({ pathname: successPath, state: complaintId });
        } catch (error) {
          Sentry.captureException(error);
          Sentry.captureMessage('Error: Error submitting complaint');
          history.push({ pathname: errorPath });
        } finally {
          setIsLoading(false);
        }
      }

      setIsLoading(true);

      if (typesSelected) {
        const currentComplaintTypes = selectedComplaintTypes;
        const files = selectedFiles.map((file) => ({
          name: file.name,
          mimetype: file.type,
        }));
        axiosPostS3Token(currentComplaintTypes, files);
      } else {
        scrollToTypes();
        setShowTypeError(true);
        setIsLoading(false);
      }
    },
    [
      typesSelected,
      stats?.cityInfo?.place,
      address,
      apn,
      selectedFiles,
      selectedComplaintTypes,
      scrollToTypes,
    ]
  );

  return (
    <ComplaintsWrapper>
      <form onSubmit={handleSubmit(memoizedOnSubmit)}>
        <Disclaimer>
          {isString(get(stats, 'form.header_text')) && (
            <HeaderNote data-cy="form-header-text">
              {parse(get(stats, 'form.header_text') as string)}
              {' '}Otherwise, please fill out the form below to file a complaint about a
              short-term rental property.
            </HeaderNote>
          )}
        </Disclaimer>

        <ComplaintType
          refProp={complaintTypeRef}
          showTypeError={showTypeError}
          setShowTypeError={setShowTypeError}
        />

        <OffendingProperty control={control} errors={errors} stats={stats} />

        <PriorComplaint
          control={control}
          errors={errors}
          watch={watch}
          register={register}
          unregister={unregister}
          params={get(stats, 'form.standardSections.priorComplaints')}
        />

        <ComplaintDescription control={control} errors={errors} />

        <ComplainantInfo
          control={control}
          errors={errors}
          params={get(stats, 'form.standardSections.complainantInfo')}
        />

        <Uploads params={get(stats, 'form.standardSections.fileUpload')} />

        <OptionalInfo control={control} errors={errors} />

        <TermsAndConditions
          control={control}
          name={FormInputTypes.tosVersionAccepted}
          error={errors[FormInputTypes.tosVersionAccepted]}
        />

        <SubmissionModule isLoading={isLoading} />
      </form>
    </ComplaintsWrapper>
  );
}

export function ComplaintsForm(props: IProps): JSX.Element {
  const { stats } = props;

  return (
    <ComplaintFormProvider>
      <ComplaintsFormComponent stats={stats} />
    </ComplaintFormProvider>
  );
}
